// Field speed events -- the one place that owns their names, their required
// fields and the build stamp every one of them carries (card #4671, ADR-0174
// decision 1).
//
// INSTRUMENTATION_VERSION: bump this whenever the shape or meaning of ANY
// speed event changes -- a name, a field, a unit, or when it fires. A logging
// change must never again look like a speed change; the two versions on every
// row let a query separate "the app got slower" from "we measure it
// differently now".
//
// Every row's `detail` JSON carries `appVersion` and `instrumentationVersion`,
// stamped when the event is CREATED. field_log_sync.js sends app_version per
// upload batch, so a row queued offline on an old build and shipped after the
// phone updated would be mislabelled by it. Do not rely on the batch value.
//
// Contexts: a page reads its version from <meta name="app-version">
// (runningAppVersion). The service worker has no document, so its call site
// passes its own build-time APP_VERSION constant as `version`. APP_VERSION is
// an esbuild define in bin/build-sw only, so it cannot be referenced here.
//
// The stamp keys are written first on purpose: app_logger truncates the
// serialised detail at 1000 characters, so a long row loses its tail, never
// its version.
//
// Measurement only. No personal data goes in a speed event (no names, emails
// or tasker ids).
import { logEvent, logWarn } from "../field_logger"
import { runningAppVersion } from "../field_app_version"

export const INSTRUMENTATION_VERSION = 1

export const SCREEN_LOADED                 = "Screen loaded"
export const SCREEN_REQUEST_FAILED         = "Screen request failed"
export const NAVIGATION_ANSWERED           = "Navigation answered"
export const CONNECTIVITY_ENTERED_DEGRADED = "Connectivity entered degraded"
export const SPEED_TEST_COMPLETED          = "Speed test completed"
export const FULL_SYNC_COMPLETE            = "Full sync complete"

// Also emitted through the same call sites, so also stamped. Not one of the
// six contract events the daily rollup reads.
export const SCREEN_LOAD_CANCELLED         = "Screen load cancelled"
export const CONNECTIVITY_ENTERED_OFFLINE  = "Connectivity entered offline"

// category = app_logs.category; required = fields every emission carries
// (call sites may add more, e.g. phases, engine, via, stats).
export const SPEED_EVENTS = {
  [SCREEN_LOADED]:                 { category: "nav",          required: ["path", "ms", "source", "via", "level"] },
  [SCREEN_REQUEST_FAILED]:         { category: "nav",          required: ["path", "ms", "reason", "phase", "level"] },
  [NAVIGATION_ANSWERED]:           { category: "nav",          required: ["path", "source", "ms"] },
  [CONNECTIVITY_ENTERED_DEGRADED]: { category: "connectivity", required: ["level", "previousLevel"] },
  [SPEED_TEST_COMPLETED]:          { category: "network",      required: ["avgMs", "packetLossPercent", "pings"] },
  [FULL_SYNC_COMPLETE]:            { category: "sync",         required: ["durationMs", "level"] },
  [SCREEN_LOAD_CANCELLED]:         { category: "nav",          required: ["path", "ms", "phase"] },
  [CONNECTIVITY_ENTERED_OFFLINE]:  { category: "connectivity", required: ["level", "previousLevel"] }
}

// { appVersion, instrumentationVersion } to merge into a speed event's detail.
// `version` is for contexts with no document (the service worker).
export function speedStamp(version = null) {
  return {
    appVersion: version || runningAppVersion(),
    instrumentationVersion: INSTRUMENTATION_VERSION
  }
}

// Logs a speed event with the stamp first in `detail`. `level: "warn"` keeps
// the warn severity the one caller that needs it ("Full sync complete" with
// missions left off the phone) already used. Returns the logger's promise.
export function logSpeedEvent(name, detail, { version = null, level = "event" } = {}) {
  const spec = SPEED_EVENTS[name]
  if (!spec) return Promise.resolve() // names are constants, pinned by test; a logger never throws

  const write = level === "warn" ? logWarn : logEvent
  return write(spec.category, name, { ...speedStamp(version), ...detail })
}
